import { useEffect, useState } from "react";
import { Sun, CloudSun, Cloud, CloudFog, CloudDrizzle, CloudRain, CloudSnow, CloudLightning, MapPin, Globe, Wind } from "lucide-react";

const WMO = (c) => {
  if (c === 0) return ["Clear sky", Sun];
  if (c <= 2) return ["Partly cloudy", CloudSun];
  if (c === 3) return ["Overcast", Cloud];
  if (c <= 48) return ["Fog", CloudFog];
  if (c <= 57) return ["Drizzle", CloudDrizzle];
  if (c <= 67) return ["Rain", CloudRain];
  if (c <= 77) return ["Snow", CloudSnow];
  if (c <= 82) return ["Showers", CloudRain];
  if (c <= 86) return ["Snow showers", CloudSnow];
  return ["Thunderstorm", CloudLightning];
};

async function fetchLocation() {
  const c = sessionStorage.getItem("lw.loc");
  if (c) return JSON.parse(c);
  let loc;
  try {
    const j = await (await fetch("https://ipwho.is/")).json();
    if (j.success === false) throw new Error("ipwho");
    loc = { ip: j.ip, city: j.city, country: j.country, lat: j.latitude, lon: j.longitude };
  } catch {
    const j = await (await fetch("https://ipapi.co/json/")).json();
    loc = { ip: j.ip, city: j.city, country: j.country_name, lat: j.latitude, lon: j.longitude };
  }
  sessionStorage.setItem("lw.loc", JSON.stringify(loc));
  return loc;
}

async function fetchWeather(lat, lon) {
  const c = JSON.parse(sessionStorage.getItem("lw.wx") || "null");
  if (c && Date.now() - c.at < 20 * 60 * 1000) return c.data;
  const u = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weather_code,wind_speed_10m&daily=weather_code,temperature_2m_max,temperature_2m_min&timezone=auto&forecast_days=4`;
  const data = await (await fetch(u)).json();
  sessionStorage.setItem("lw.wx", JSON.stringify({ at: Date.now(), data }));
  return data;
}

const useClock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  return now;
};

const useLiveInfo = () => {
  const [loc, setLoc] = useState(() => JSON.parse(sessionStorage.getItem("lw.loc") || "null"));
  const [wx, setWx] = useState(() => JSON.parse(sessionStorage.getItem("lw.wx") || "null")?.data || null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let t;
    const load = async () => {
      try {
        const l = await fetchLocation();
        setLoc(l);
        setWx(await fetchWeather(l.lat, l.lon));
      } catch { setErr(true); }
    };
    load();
    t = setInterval(load, 30 * 60 * 1000);
    return () => clearInterval(t);
  }, []);
  return { loc, wx, err };
};

const Forecast = ({ wx }) => (
  <div className="hidden sm:flex gap-3" data-testid="widget-forecast">
    {wx.daily.time.slice(1, 4).map((d, i) => {
      const [label, Icon] = WMO(wx.daily.weather_code[i + 1]);
      return (
        <div key={d} className="flex flex-col items-center text-[11px] text-muted-foreground" title={label}>
          <span>{new Date(d + "T00:00").toLocaleDateString(undefined, { weekday: "short" })}</span>
          <Icon className="h-4 w-4 my-0.5 text-foreground" strokeWidth={1.75} />
          <span className="tabular-nums">{Math.round(wx.daily.temperature_2m_max[i + 1])}°/{Math.round(wx.daily.temperature_2m_min[i + 1])}°</span>
        </div>
      );
    })}
  </div>
);

export const InfoWidget = ({ compact }) => {
  const now = useClock();
  const { loc, wx, err } = useLiveInfo();
  const [label, Icon] = wx ? WMO(wx.current.weather_code) : ["", Cloud];
  const day = now.toLocaleDateString(undefined, { weekday: "long" });
  const date = now.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
  const time = now.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const place = loc ? [loc.city, loc.country].filter(Boolean).join(", ") : err ? "Location unavailable" : "Locating…";

  if (compact) {
    return (
      <div className="hidden md:flex items-center gap-2 text-xs text-muted-foreground" data-testid="info-widget-compact">
        <span className="tabular-nums">{now.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })} · {now.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</span>
        {loc && <span>· {loc.city}</span>}
        {wx && <span className="flex items-center gap-1">· <Icon className="h-3.5 w-3.5" />{Math.round(wx.current.temperature_2m)}°C</span>}
      </div>
    );
  }

  return (
    <div className="lw-card flex flex-wrap items-stretch gap-x-6 gap-y-3 px-5 py-3.5" data-testid="info-widget">
      <div className="min-w-[150px]">
        <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground" data-testid="widget-day">{day}</div>
        <div className="text-sm font-medium" data-testid="widget-date">{date}</div>
        <div className="text-2xl font-semibold tabular-nums tracking-tight" data-testid="widget-time">{time}</div>
      </div>
      <div className="w-px bg-border hidden 2xl:block" />
      <div className="flex flex-col justify-center gap-1.5 text-sm min-w-[170px]">
        <div className="flex items-center gap-2" data-testid="widget-location"><MapPin className="h-4 w-4 text-primary" />{place}</div>
        <div className="flex items-center gap-2 text-muted-foreground" data-testid="widget-ip"><Globe className="h-4 w-4" />IP {loc?.ip || "—"}</div>
      </div>
      <div className="w-px bg-border hidden 2xl:block" />
      <div className="flex items-center gap-5" data-testid="widget-weather">
        {wx ? (
          <>
            <div className="flex items-center gap-2.5">
              <Icon className="h-8 w-8 text-primary" strokeWidth={1.5} />
              <div>
                <div className="text-xl font-semibold tabular-nums" data-testid="widget-temp">{Math.round(wx.current.temperature_2m)}°C</div>
                <div className="text-xs text-muted-foreground flex items-center gap-1">{label} · <Wind className="h-3 w-3" />{Math.round(wx.current.wind_speed_10m)} km/h</div>
              </div>
            </div>
            <Forecast wx={wx} />
          </>
        ) : (
          <div className="text-sm text-muted-foreground">{err ? "Weather unavailable" : "Loading weather…"}</div>
        )}
      </div>
    </div>
  );
};
